import Link from "next/link"

import { Button } from "@coval/ui/button"
import { Input } from "@coval/ui/input"
import { Label } from "@coval/ui/label"

const MAX_ORG_NAME_LENGTH = 200

interface OrganizationNameFormProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  heading?: string
  description?: string
  submitLabel?: string
  signInHref?: string
}

export function OrganizationNameForm({
  value,
  onChange,
  onSubmit,
  heading = "Create your organization",
  description = "Your team’s workspace for testing and monitoring AI agents.",
  submitLabel = "Continue",
  signInHref,
}: OrganizationNameFormProps) {
  const trimmed = value.trim()
  const canSubmit = trimmed.length > 0 && trimmed.length <= MAX_ORG_NAME_LENGTH

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (canSubmit) {
      onSubmit()
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6"
      aria-label="Create your organization"
      data-testid="signup-organization-form"
    >
      <div className="space-y-1 text-center">
        <h1 className="text-xl font-medium text-coval-text">{heading}</h1>
        <p className="text-sm leading-5 text-coval-text-muted">{description}</p>
      </div>

      <div className="space-y-1">
        <Label className="font-medium" htmlFor="signup-organization-name">
          Organization name
        </Label>
        <Input
          id="signup-organization-name"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={MAX_ORG_NAME_LENGTH}
          required
          aria-label="Organization name"
          data-testid="signup-organization-name-input"
        />
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={!canSubmit}
        data-testid="signup-organization-submit-button"
      >
        {submitLabel}
      </Button>

      {signInHref && (
        <p className="text-center text-sm text-coval-text-muted">
          Already have an account?{" "}
          <Link href={signInHref} className="underline hover:text-coval-text">
            Sign in
          </Link>
        </p>
      )}
    </form>
  )
}
